/**
 * การพิมพ์และการเปิดลิ้นชัก
 *
 * แยกออกจากหน้าจอเพราะเป็นลำดับขั้นตอนที่ต้องเหมือนกันทุกที่ที่เรียกใช้
 * และเพราะกติกาข้อสำคัญข้อหนึ่งอยู่ตรงนี้ —
 *
 * ★ พิมพ์ไม่ออกไม่เป็นเหตุให้ปิดบิลไม่ได้ ★
 * ทุกฟังก์ชันคืนข้อความเตือนแทนที่จะโยน error ให้หน้าจอที่เรียกล้ม
 */

import { kickDrawer, printImage } from './lib/bridge.ts';
import {
  renderBillSlip,
  renderDrawerSlip,
  renderKitchenSlip,
  type DrawerSlipInput,
} from './lib/slip.ts';
import type { Store } from './store.tsx';
import type { SaleDetail, SaleLine } from './lib/types.ts';

/** ป้ายบอกว่าบิลนี้ของใคร — ขึ้นหัวใบครัวและใบเสร็จ */
export function whereLabel(store: Store, sale: SaleDetail): string {
  if (sale.billType === 'dine_in') {
    const table = store.tables.find((t) => t.id === sale.diningTableId);
    return table ? `โต๊ะ ${table.name}` : 'ทานที่ร้าน';
  }
  if (sale.billType === 'takeaway') return 'กลับบ้าน';

  const channel = store.deliveryChannels.find((c) => c.id === sale.deliveryChannelId);
  const name = channel?.name ?? 'เดลิเวอรี่';
  return sale.deliveryOrderRef ? `${name} #${sale.deliveryOrderRef}` : name;
}

/**
 * ชื่ออุปกรณ์บลูทูธที่จะให้ APK ไปหา
 *
 * ★ ไม่ใช่ printer.name ★ name เป็นชื่อที่คนอ่าน ยกมาจากระบบเดิม เช่น "Printer001"
 * ส่วนชื่อที่ Android เห็นตอนค้นอุปกรณ์ที่จับคู่ไว้คือ "InnerPrinter" — คนละอย่าง
 * ว่างไว้ = ให้ APK ใช้ตัวแรกที่จับคู่ เพราะร้านมีเครื่องพิมพ์สาขาละตัว
 */
function printerTarget(store: Store, printerId: number): string {
  return store.printers.find((p) => p.id === printerId)?.btDeviceName ?? '';
}

function paperWidth(store: Store, printerId?: number): number {
  const printer = printerId === undefined
    ? store.printers[0]
    : store.printers.find((p) => p.id === printerId);
  return printer?.paperWidthMm ?? 80;
}

/**
 * ส่งรายการเข้าครัว
 *
 * ★ แยกใบตามครัว และพิมพ์เฉพาะรอบที่ระบุ ★
 * ทั้งสองสาขามีเครื่องพิมพ์ตัวเดียว แต่ครัวร้อนกับครัวเย็นต้องได้คนละใบ
 * ถ้าพิมพ์ทั้งบิลทุกครั้งที่เพิ่มออเดอร์ ครัวจะทำอาหารซ้ำทันทีในชั่วโมงแรก
 *
 * สินค้าที่ไม่ได้ระบุครัวไว้จะไปรวมกับครัวแรกของสาขา ไม่ปล่อยให้หายไปเงียบ ๆ
 */
export async function printKitchenSlips(
  store: Store,
  sale: SaleDetail,
  orderRound: number,
): Promise<string[]> {
  const lines = sale.lines.filter((l) => l.orderRound === orderRound && !l.voided);
  if (lines.length === 0) return [];

  const kitchens = [...store.kitchens].sort((a, b) => a.sortOrder - b.sortOrder);
  if (kitchens.length === 0) return ['สาขานี้ยังไม่ได้ตั้งค่าครัว จึงไม่ได้พิมพ์ใบครัว'];

  const byKitchen = new Map<number, SaleLine[]>();
  for (const line of lines) {
    const product = store.products.find((p) => p.id === line.productId);
    const matched = kitchens.find((k) => k.name === product?.kitchenName) ?? kitchens[0]!;
    const bucket = byKitchen.get(matched.id);
    if (bucket) bucket.push(line);
    else byKitchen.set(matched.id, [line]);
  }

  const problems: string[] = [];
  const where = whereLabel(store, sale);

  for (const kitchen of kitchens) {
    const items = byKitchen.get(kitchen.id);
    if (!items?.length) continue;

    const canvas = renderKitchenSlip({
      kitchenName: kitchen.name,
      where,
      billNo: sale.billNo,
      orderRound,
      items: items.map((l) => ({ qty: l.qty, name: l.productName, note: l.note })),
      paperWidthMm: paperWidth(store, kitchen.printerId),
    });

    const result = await printImage('kitchen', canvas, {
      // ★ ใส่เลขรอบไว้ด้วย ★ โต๊ะเดียวสั่งหลายรอบจะได้ใบครัวหลายใบ
      // ถ้าชื่อเหมือนกันหมด พนักงานแยกไม่ออกว่าใบที่ค้างอยู่คือรอบไหน
      label: `ใบครัว ${kitchen.name} · รอบ ${orderRound} · ${where} · ${sale.billNo}`,
      printerName: printerTarget(store, kitchen.printerId),
    });
    if (!result.ok) problems.push(`${kitchen.name}: ${result.message}`);
  }
  return problems;
}

/**
 * ใบแจ้งยอดก่อนชำระ — มี QR พร้อมเพย์ระบุยอดไว้ในตัว
 * ลูกค้าสแกนแล้วโอนได้เลยโดยไม่ต้องพิมพ์ตัวเลขเอง
 */
export function billSlipCanvas(store: Store, sale: SaleDetail, withQr: boolean) {
  return renderBillSlip({
    branch: {
      name: store.branch.name,
      address: store.branch.address,
      phone: store.branch.phone,
      promptpayId: store.branch.promptpayId,
    },
    billNo: sale.billNo,
    receiptNo: sale.receiptNo,
    where: whereLabel(store, sale),
    guestCount: sale.guestCount,
    businessDate: sale.businessDate,
    items: sale.lines
      .filter((l) => !l.voided)
      .map((l) => ({
        qty: l.qty,
        name: l.productName,
        note: l.note,
        lineTotalSatang: l.lineTotalSatang,
      })),
    subtotalSatang: sale.subtotalSatang,
    discounts: sale.discounts.map((d) => ({ label: d.label, amountSatang: d.amountSatang })),
    totalSatang: sale.totalSatang,
    payments: sale.payments
      .filter((p) => p.kind === 'payment')
      .map((p) => ({ name: p.methodName, amountSatang: p.amountSatang })),
    changeSatang: sale.payments.find((p) => p.changeSatang)?.changeSatang ?? undefined,
    withQr,
    paperWidthMm: paperWidth(store),
  });
}

export async function printBillSlip(
  store: Store,
  sale: SaleDetail,
  withQr: boolean,
): Promise<string | null> {
  const canvas = billSlipCanvas(store, sale, withQr);
  const result = await printImage(sale.receiptNo ? 'receipt' : 'bill', canvas, {
    label: `${sale.receiptNo ? 'ใบเสร็จ' : 'ใบแจ้งยอด'} ${sale.receiptNo ?? sale.billNo}`,
    printerName: printerTarget(store, store.kitchens[0]?.printerId ?? 0),
  });
  return result.ok ? null : (result.message ?? 'พิมพ์ไม่สำเร็จ');
}

export async function printDrawerSummary(
  store: Store,
  input: Omit<DrawerSlipInput, 'branchName' | 'paperWidthMm'>,
): Promise<string | null> {
  const canvas = renderDrawerSlip({
    ...input,
    branchName: store.branch.name,
    paperWidthMm: paperWidth(store),
  });
  const result = await printImage('receipt', canvas, {
    label: `สรุปปิดรอบ ${input.docNo}`,
    printerName: printerTarget(store, store.kitchens[0]?.printerId ?? 0),
  });
  return result.ok ? null : (result.message ?? 'พิมพ์ไม่สำเร็จ');
}

/**
 * เปิดลิ้นชัก — เรียกเฉพาะเมื่อ API บอกว่าต้องเปิดเท่านั้น
 *
 * ★ ห้ามเปิดเองตามใจหน้าจอ ★ ระบบเดิมเปิดทุกครั้งที่ปิดบิล ทั้งที่โอนเงิน
 * และเดลิเวอรี่รวมกันคือ 79% ของรายได้ ซึ่งไม่มีเงินสดเข้าลิ้นชักเลย
 */
export async function openDrawer(
  store: Store,
  shouldOpen: boolean,
  reason: string | null,
): Promise<string | null> {
  if (!shouldOpen) return null;
  // ลิ้นชักต่ออยู่กับพอร์ต 24V ที่ตัวเครื่องพิมพ์ จึงต้องระบุเครื่องเดียวกัน
  const result = await kickDrawer(
    reason ?? 'unspecified',
    printerTarget(store, store.kitchens[0]?.printerId ?? 0),
  );
  return result.ok ? null : (result.message ?? 'เปิดลิ้นชักไม่สำเร็จ');
}
